import { useEffect, useState } from 'react'
import { CalendarClock, Check, FileText, FolderOpen, Paperclip } from 'lucide-react'
import { completeStewardJob, getStewardJobCard, type StewardJobBlock, type StewardJobCard, type StewardJobFile } from '@/lib/api'
import { useStewardStore } from '@/stores/useStewardStore'
import { Markdown } from './Markdown'

/** The parent Project's Johnny.Decimal ID (`NN.NN`) read off the Job's path —
 *  the JD wiring the constitution numbers by (`10-19 Area / 10 Category / 10.01 Project / job`).
 *  Returns null for a Job not yet under a numbered Project. */
function jdId(path: string): string | null {
  for (const seg of path.split('/')) {
    const m = /^(\d\d\.\d\d) /.exec(seg)
    if (m) return m[1]
  }
  return null
}

function blockLabel(b: StewardJobBlock): string {
  const when = b.all_day || !b.time
    ? `all-day${b.date ? ` ${b.date}` : ''}`
    : `${b.date ?? ''} ${b.time}${b.duration_minutes ? ` (${b.duration_minutes}m)` : ''}`.trim()
  return b.label ? `${b.label} — ${when}` : when
}

interface Props {
  path: string
  onOpenFile: (file: { path: string; name: string; ext: string }) => void
}

/**
 * A Job opened as a card in the chat — the do-it brief (job.md) plus its working
 * links (the files in the folder). Clicking a file opens it as its own card. The
 * card reloads from the API (only the path is held in the store).
 */
export function JobCard({ path, onOpenFile }: Props) {
  const [job, setJob] = useState<StewardJobCard | null>(null)
  const [error, setError] = useState<string | null>(null)
  const sessionId = useStewardStore((s) => s.sessionId)
  const [completing, setCompleting] = useState(false)
  const [closedMsg, setClosedMsg] = useState<string | null>(null)
  const [completeErr, setCompleteErr] = useState<string | null>(null)

  const onDone = async () => {
    setCompleting(true)
    setCompleteErr(null)
    try {
      const r = await completeStewardJob(path, sessionId)
      setClosedMsg(r.message || 'Closing out — the outcome will land in your Inbox.')
    } catch (e) {
      setCompleteErr(e instanceof Error ? e.message : 'Could not close out')
    } finally {
      setCompleting(false)
    }
  }

  useEffect(() => {
    let alive = true
    getStewardJobCard(path)
      .then((j) => alive && setJob(j))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Failed to load job'))
    return () => {
      alive = false
    }
  }, [path])

  // A Job that's already wrapped (completed) or archived can't be closed out again —
  // show its state instead of a live Done button.
  const status = job ? String(job.frontmatter?.status ?? '') : ''
  const alreadyDone = status === 'completed' || status === 'archived'

  return (
    <div className="max-w-[85%] rounded-2xl border border-border bg-muted/20 p-4 text-[15px] leading-relaxed text-foreground">
      <div className="mb-2 flex items-center justify-between gap-2 text-[12px] font-light text-muted-foreground">
        <div className="flex min-w-0 items-center gap-2">
          <FolderOpen className="h-3.5 w-3.5 shrink-0" />
          {jdId(path) && (
            <span className="shrink-0 font-mono tabular-nums text-muted-foreground/70">{jdId(path)}</span>
          )}
          <span className="truncate">{job?.title || (error ? 'Job' : 'Loading…')}</span>
        </div>
        {job && !error && (
          closedMsg ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-emerald-600">
              <Check className="h-3 w-3" /> Closing out
            </span>
          ) : alreadyDone ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
              <Check className="h-3 w-3" /> {status === 'archived' ? 'Archived' : 'Completed'}
            </span>
          ) : (
            <button
              type="button"
              onClick={onDone}
              disabled={completing}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border bg-background px-2 py-0.5 text-[11px] font-light text-foreground hover:bg-muted/50 disabled:opacity-60"
            >
              <Check className="h-3 w-3" /> {completing ? 'Closing out…' : 'Done'}
            </button>
          )
        )}
      </div>

      {error ? (
        <div className="text-[13px] text-red-500/90">Couldn't open this job: {error}</div>
      ) : !job ? (
        <div className="text-[13px] font-light text-muted-foreground">Loading the brief…</div>
      ) : (
        <>
          {job.body ? (
            <Markdown source={job.body} className="steward-prose" />
          ) : (
            <div className="text-[13px] font-light text-muted-foreground">(empty brief)</div>
          )}

          {job.blocks && job.blocks.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-light text-muted-foreground">
                <CalendarClock className="h-3 w-3" /> {job.blocks.length === 1 ? 'Scheduled' : `Schedule · ${job.blocks.length} blocks`}
              </div>
              <div className="flex flex-col gap-1">
                {job.blocks.map((b, i) => (
                  <div key={i} className="text-[13px] font-light text-foreground">
                    {blockLabel(b)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {job.files.length > 0 && (
            <div className="mt-3 border-t border-border pt-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-light text-muted-foreground">
                <Paperclip className="h-3 w-3" /> Files
              </div>
              <div className="flex flex-wrap gap-1.5">
                {job.files.map((f: StewardJobFile) => (
                  <button
                    key={f.rel}
                    type="button"
                    onClick={() => onOpenFile({ path: f.rel, name: f.name, ext: f.ext })}
                    className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1 text-[12px] font-light hover:bg-muted/50"
                  >
                    <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="max-w-[220px] truncate">{f.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {closedMsg && (
        <div className="mt-3 border-t border-border pt-2 text-[12px] font-light text-muted-foreground">
          {closedMsg}
        </div>
      )}
      {completeErr && (
        <div className="mt-2 text-[12px] text-red-500/90">Couldn't close out: {completeErr}</div>
      )}
    </div>
  )
}
